import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { Plus, Trash2, X, ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogClose,
} from "../../components/ui/dialog";
import {
  addGalleryEntry,
  getAllGalleryEntries,
  deleteGalleryImages,
} from "../../services/api";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCustomToast } from "../../hooks/useToast";
import { GalleryImage } from "../../types/gallery";

const isHeic = (file: File) =>
  file.type === "image/heic" ||
  file.type === "image/heif" ||
  file.name.toLowerCase().endsWith(".heic") ||
  file.name.toLowerCase().endsWith(".heif");

const ITEMS_PER_PAGE = 12;

const Gallery = () => {
  const [items, setItems] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadPreviews, setUploadPreviews] = useState<(string | undefined)[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [viewImage, setViewImage] = useState<GalleryImage | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useCustomToast();

  const fetchGalleryEntries = useCallback(async () => {
    try {
      const response = (await getAllGalleryEntries()) as any;
      setItems(response.data.data ?? []);
    } catch {
      showError("Failed to fetch gallery images.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGalleryEntries();
  }, [fetchGalleryEntries]);

  // Pagination
  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  const paginatedItems = items.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Upload handlers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploadFiles((prev) => [...prev, ...files]);
    setUploadPreviews((prev) => [
      ...prev,
      ...files.map((f) => (isHeic(f) ? undefined : URL.createObjectURL(f))),
    ]);
    // reset input so same files can be re-selected
    e.target.value = "";
  };

  const removeUploadPreview = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      uploadFiles.forEach((file) => formData.append("images[]", file));
      const response = (await addGalleryEntry(formData)) as any;
      const message = response.data?.message ?? "Images uploaded successfully.";
      showSuccess(message);
      resetUploadForm();
      setIsUploadOpen(false);
      setIsLoading(true);
      await fetchGalleryEntries();
    } catch {
      showError("Failed to upload images.");
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFiles([]);
    setUploadPreviews([]);
  };

  // Delete handlers
  const handleDeleteSingle = async (id: string) => {
    try {
      await deleteGalleryImages([id]);
      showSuccess("Image deleted.");
      setItems((prev) => prev.filter((img) => img.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch {
      showError("Failed to delete image.");
    }
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    try {
      await deleteGalleryImages(ids);
      showSuccess(`${ids.length} image${ids.length > 1 ? "s" : ""} deleted.`);
      setItems((prev) => prev.filter((img) => !selectedIds.has(img.id)));
      setSelectedIds(new Set());
    } catch {
      showError("Failed to delete selected images.");
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-8">
      <ToastContainer />
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Gallery</h1>
          <p className="text-muted-foreground">
            Let's showcase the beauty of charmé
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <Button
              variant="outline"
              className="border-red-400 text-red-500 hover:bg-red-50"
              onClick={handleDeleteSelected}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Selected ({selectedIds.size})
            </Button>
          )}
          <Button
            className="bg-[#B87775] hover:bg-[#B87775]/90 text-white"
            onClick={() => setIsUploadOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            UPLOAD IMAGES
          </Button>
        </div>
      </div>

      {/* Grid */}
      <Card className="p-6">
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B87775]" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
            <ImageIcon className="h-12 w-12 opacity-30" />
            <p>No images yet. Upload some to get started.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {paginatedItems.map((img) => {
                const isSelected = selectedIds.has(img.id);
                return (
                  <div
                    key={img.id}
                    className={`relative group aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      isSelected
                        ? "border-[#B87775] shadow-md"
                        : "border-transparent"
                    }`}
                    onClick={() => setViewImage(img)}
                  >
                    <img
                      src={img.url}
                      alt={img.caption || img.formatted_date}
                      className="h-full w-full object-cover"
                    />

                    {/* Overlay controls */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all" />

                    {/* Checkbox */}
                    <button
                      className={`absolute top-2 left-2 h-5 w-5 rounded border-2 flex items-center justify-center transition-all z-10 ${
                        isSelected
                          ? "bg-[#B87775] border-[#B87775]"
                          : "bg-white/80 border-gray-300 opacity-0 group-hover:opacity-100"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(img.id);
                      }}
                    >
                      {isSelected && (
                        <svg
                          className="h-3 w-3 text-white"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>

                    {/* Delete button */}
                    <button
                      className="absolute top-2 right-2 h-7 w-7 rounded-full bg-[#B87775] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-[#a06563]"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSingle(img.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Upload Modal */}
      <Dialog
        open={isUploadOpen}
        onOpenChange={(open) => {
          setIsUploadOpen(open);
          if (!open) resetUploadForm();
        }}
      >
        <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
          <DialogHeader className="p-0">
            <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-6 w-6 text-[#B87775]" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </DialogHeader>

          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Upload Images</h2>

            {/* Drop zone */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[#B87775] hover:bg-[#B87775]/5 transition-colors mb-4"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImageIcon className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-sm text-muted-foreground text-center">
                Click to select images
                <br />
                <span className="text-xs">
                  Multiple files supported
                </span>
              </p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              multiple
              className="hidden"
            />

            {/* Previews */}
            {uploadPreviews.length > 0 && (
              <div className="grid grid-cols-4 gap-3 mb-6">
                {uploadPreviews.map((src, i) => (
                  <div
                    key={i}
                    className="relative aspect-square rounded-md overflow-hidden bg-gray-100"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={`preview-${i}`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex flex-col items-center justify-center gap-1 text-gray-400">
                        <ImageIcon className="h-6 w-6" />
                        <span className="text-[10px] text-center px-1">No preview available</span>
                      </div>
                    )}
                    <button
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"
                      onClick={() => removeUploadPreview(i)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                className="border-[#B87775] text-[#B87775] uppercase"
                onClick={() => {
                  resetUploadForm();
                  setIsUploadOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploadFiles.length === 0 || isUploading}
                className="bg-[#B87775] hover:bg-[#B87775]/90 text-white uppercase"
              >
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Image Modal */}
      <Dialog open={!!viewImage} onOpenChange={(open) => !open && setViewImage(null)}>
        <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden">
          <DialogHeader className="p-0">
            <DialogClose className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-6 w-6 text-white drop-shadow" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </DialogHeader>
          {viewImage && (
            <div className="flex flex-col">
              <div className="bg-black flex items-center justify-center" style={{ maxHeight: "70vh" }}>
                <img
                  src={viewImage.url}
                  alt={viewImage.caption || viewImage.formatted_date}
                  className="max-h-[70vh] w-full object-contain"
                />
              </div>
              {(viewImage.caption || viewImage.formatted_date) && (
                <div className="p-4">
                  {viewImage.caption && (
                    <p className="text-sm font-medium">{viewImage.caption}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {viewImage.formatted_date}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
